// The ownership contract (INV-LEVEL): where block content lands at a caret in a text row. Every block-inserting gesture
// (paste, slash insert, the insert commands) places through here; callers flatten or refuse first in an inline-only
// slot (levels.ts), where no block has a place.
import type { Block, Blocks } from '@w/content-blocks/src/block'
import { insertBlockAfter, insertBlocksAfter, spliceBlocks, takeInlineTail } from './ops'
import type { CorePoint, CoreState } from './state'
import { blockClass } from './block-selection'
import { children, gapOf, gapPoint, isRow, plain, pointAt, rows, type Element, type Row } from './tree'

export interface PlaceOptions {
  /** A text paste: a text row at either end of `blocks` joins the half of the split row beside it. */
  merge?: boolean
  /** A paste: in a list item, pasted paragraphs become sibling items (Notion). */
  items?: boolean
}

/**
 * Places `blocks` at `offset` in `row`, a text row of a block-level container. The row splits at the caret and the
 * blocks land between its halves, in the row's own block list; an emptied half goes (an empty row is replaced). In a
 * list item, a pasted list (not a toggle) at the item's own text row leaves the item whole, an empty one included: its
 * items follow the item, after the item's own blocks, as siblings in its list (Notion); at a nonempty item's start
 * they go before it. Other list items there (a list at a later row of the item; pasted paragraphs with `items`) land as
 * sibling items with the item split around them: a block after an item's row belongs to that item, and the text after
 * the caret, with the item's blocks after its row, starts the next item. Returns where the caret goes: the end of the
 * last placed row, else the gap after the last placed block, or the end of the joined text for a merge.
 */
export function placeBlocks(state: CoreState, row: Row, offset: number, blocks: Blocks, options: PlaceOptions = {}): CorePoint {
  const item = row.parents.at(-1)
  const list = row.parents.at(-2)
  const inItem = item?.type === 'li' && list?.type === 'list' && list.subtype !== 'toggle'
  let seq = inItem ? blocks.flatMap((block): Blocks => block.type === 'list' && block.subtype !== 'toggle' ? block.children
    : options.items && item.children[0] === row.block && block.type === 'p' ? [{ id: '', type: 'li', children: [block] }] : [block]) : [...blocks]
  const empty = row.block.children.length === 0
  // Pasted list items at a list item's own text row leave the item whole, an empty one included: they follow it, after its
  // own blocks, as siblings in its list (Notion browser checks notion-browser-queue.md 30 and 65, imp03/imp07: `""` /
  // `"a"` / `"b"` bullets; `yz` keeps `c`, the pasted tree after it). At a nonempty item's start they go before it.
  if (inItem && options.items && item.children[0] === row.block && blocks.length > 0 && blocks.every(block => block.type === 'list' && block.subtype !== 'toggle') &&
    (empty || offset > 0)) {
    insertBlocksAfter(state, list.children, item, seq)
    const last = rows(seq).at(-1)
    if (last) return pointAt(last, plain(last.block).length)
  }
  const textRow = (block: Block | undefined): Element | undefined => block && ['p', 'h', 'pre'].includes(block.type) && isRow(block)
    ? block as Element : block?.type === 'li' && block.children[0] && isRow(block.children[0]) && block.children[0].type === 'p'
      ? block.children[0] : undefined
  // An empty row takes the first block as it is (Lexical replaces an empty paragraph); a paragraph joins it either way.
  const canMerge = options.merge === true && seq.length > 1 && !!textRow(seq[0]) && !!textRow(seq.at(-1)) &&
    (!empty || seq[0].type === 'p' || seq[0].type === 'li')
  let head: Element | undefined = row.block
  let tail: Element | undefined
  let caret: CorePoint | undefined
  if (canMerge) {
    const [first] = seq
    const firstRow = textRow(first) as Element
    const rest = takeInlineTail(state, row.block.children, offset)
    spliceBlocks(state, row.block.children, row.block.children.length, 0, ...firstRow.children)
    // A merged item's other blocks stay with the item holding the caret row.
    seq = [...(first.type === 'li' ? first.children.slice(1) : []), ...seq.slice(1)]
    const last = textRow(seq.at(-1)) as Element
    const end = plain(last).length
    last.children.push(...rest)
    caret = { blockId: '', offset: end }
    tail = last
  } else if (offset === 0) {
    head = undefined
    tail = empty ? undefined : row.block
  } else {
    const rest = takeInlineTail(state, row.block.children, offset)
    tail = rest.length > 0 ? { ...row.block, id: '', children: rest } as Element : undefined
  }
  // The caret's row is the last placed row as placed, before an item takes the blocks after the caret.
  const lastRow = rows(seq).at(-1)
  const lastLength = lastRow ? plain(lastRow.block).length : 0
  const lastBlock = seq.at(-1)
  if (inItem && seq.some(block => block.type === 'li')) placeItems(state, row, item, list, head, tail, seq, canMerge)
  else placeInList(state, row, head, tail, seq, canMerge)
  if (caret && tail) return pointAt(rows([tail])[0], caret.offset)
  if (lastRow) return pointAt(lastRow, lastLength)
  const gap = gapOf(state.blocks, { blockId: lastBlock?.id ?? '', offset: 1 })
  if (!gap) throw new Error('INV-1: a placed block is missing from its block list')
  return gapPoint(gap)
}

/** The blocks in the row's own block list: [head, ...blocks, tail]. */
function placeInList(state: CoreState, row: Row, head: Element | undefined, tail: Element | undefined, seq: Blocks,
  merged: boolean): void {
  const siblings = row.siblings
  const index = siblings.indexOf(row.block)
  if (head) insertBlocksAfter(state, siblings, row.block, [...seq, ...(tail && !merged ? [tail] : [])])
  else if (tail === row.block) spliceBlocks(state, siblings, index, 0, ...seq)
  else spliceBlocks(state, siblings, index, 1, ...seq)
}

/** List items land as siblings of the row's item, the item split around them. */
function placeItems(state: CoreState, row: Row, item: Element, list: Element, head: Element | undefined,
  tail: Element | undefined, seq: Blocks, merged: boolean): void {
  const firstItem = seq.findIndex(block => block.type === 'li')
  const prefix = seq.slice(0, firstItem)
  const pasted: Element[] = []
  for (const block of seq.slice(firstItem)) {
    if (block.type === 'li') pasted.push(block)
    else pasted.at(-1)?.children.push(block)
  }
  const at = item.children.indexOf(row.block)
  // At an item's start, the items go before it: the item keeps its row and blocks.
  if (!head && tail === row.block && at === 0 && prefix.length === 0) {
    spliceBlocks(state, list.children, list.children.indexOf(item), 0, ...pasted)
    return
  }
  const after = spliceBlocks(state, item.children, at + 1, item.children.length)
  if (!head) spliceBlocks(state, item.children, at, 1)
  const items: Element[] = [...pasted]
  if (merged) pasted.at(-1)?.children.push(...after)
  else if (tail) items.push({ ...item, id: '', children: [tail, ...after] } as Element)
  else if (head) spliceBlocks(state, item.children, item.children.length, 0, ...after)
  else pasted.at(-1)?.children.push(...after)
  spliceBlocks(state, item.children, item.children.length, 0, ...prefix)
  const index = list.children.indexOf(item)
  if (item.children.length === 0) spliceBlocks(state, list.children, index, 1, ...items)
  else {
    let previous = item as Block
    for (const next of items) {
      insertBlockAfter(state, list.children, previous, next)
      previous = next
    }
  }
}

/**
 * One block placed at the caret (`placeBlocks`); a headerless block (an atom, a grid) ending its block list gets an
 * empty paragraph after it to land in, as Lexical's insert-at-end convention does, unless `trailing` is false. Returns
 * the block list it landed in and its index there.
 */
export function insertAt(state: CoreState, row: Row, offset: number, block: Block, trailing = true): { home: Blocks; index: number } {
  placeBlocks(state, row, offset, [block])
  const home = listOf(state.blocks, block)
  if (!home) throw new Error('INV-1: a placed block is missing from its block list')
  const index = home.indexOf(block)
  if (trailing && ['atom', 'grid', 'header'].includes(blockClass(block)) && index === home.length - 1) {
    spliceBlocks(state, home, home.length, 0, { id: '', type: 'p', children: [] })
  }
  return { home, index }
}

/** The caret after an inserted block (Lexical `selectNext`): the next block's start, else the gap after it. */
export function after(home: Blocks, index: number): CorePoint {
  const next = home[index + 1] as Block | undefined
  const row = next && rows([next])[0]
  return row ? pointAt(row, 0) : gapPoint({ siblings: home, index: index + 1 })
}

function listOf(items: Blocks, block: Block): Blocks | null {
  if (items.includes(block)) return items
  for (const item of items) {
    const found = listOf(children(item), block)
    if (found) return found
  }
  return null
}
